
// src/utils/logger.ts
// Log levels
export enum LogLevel {
    Verbose = "Verbose",
    Debug = "Debug",
    Information = "Information",
    Warning = "Warning",
    Error = "Error",
    Fatal = "Fatal",
}

// Log interface
interface LogEntry {
    level: LogLevel;
    message: string;
    properties?: Record<string, unknown>;
    error?: Error;
}

// Main logging function
export const log = ({ level, message, properties, error }: LogEntry) => {
    const timestamp = new Date().toISOString();
    const logData = {
        ...properties,
        timestamp,
        error: error
            ? {
                  message: error.message,
                  stack: error.stack,
                  name: error.name,
              }
            : undefined,
    };

    // Log to console with appropriate method based on level
    switch (level) {
        case LogLevel.Error:
        case LogLevel.Fatal:
            console.error(`[${level}] ${message}`, logData);
            break;
        case LogLevel.Warning:
            console.warn(`[${level}] ${message}`, logData);
            break;
        case LogLevel.Debug:
            console.debug(`[${level}] ${message}`, logData);
            break;
        default:
            console.log(`[${level}] ${message}`, logData);
    }
};

// Convenience methods for different log levels
export const logVerbose = (
    message: string,
    properties?: Record<string, unknown>
) => {
    log({ level: LogLevel.Verbose, message, properties });
};

export const logDebug = (
    message: string,
    properties?: Record<string, unknown>
) => {
    log({ level: LogLevel.Debug, message, properties });
};

export const logInfo = (
    message: string,
    properties?: Record<string, unknown>
) => {
    log({ level: LogLevel.Information, message, properties });
};

export const logWarning = (
    message: string,
    properties?: Record<string, unknown>
) => {
    log({ level: LogLevel.Warning, message, properties });
};

export const logError = (
    message: string,
    error?: Error,
    properties?: Record<string, unknown>
) => {
    log({ level: LogLevel.Error, message, error, properties });
};

export const logFatal = (
    message: string,
    error?: Error,
    properties?: Record<string, unknown>
) => {
    log({ level: LogLevel.Fatal, message, error, properties });
};
